import React from 'react';
import { Star } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { localize } from '../../data/i18nData';
import { canTakeLoan, getLoyaltyLevel, getLoyaltyLabel, getAvailablePlacements, getBankSeniorityMonths } from '../../data/bankData';

export function BankCard({ bank: rawBank, relation, isSelected, onClick, reputation, vehicleCount, monthlyRevenue, treasury, totalDebt, totalDays, monthlyHistory }) {
  const bank = localize('bank', rawBank);
  const { t } = useTranslation();
  const seniorityMonths = getBankSeniorityMonths(relation, totalDays || 0);
  const loyaltyLevel = getLoyaltyLevel(bank.id, relation.loansRepaid, seniorityMonths || 0);
  const eligibility = canTakeLoan(bank.id, { totalDebt, monthlyRevenue, treasury, totalDays, monthlyHistory });
  const placements = getAvailablePlacements(bank.id, { treasury, loyaltyLevel });

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl border-2 transition-all overflow-hidden ${
        isSelected ? `${bank.borderClass} shadow-lg` : 'border-ink-600 hover:border-ink-500 '
      }`}
      data-testid={`bank-card-${bank.id}`}
    >
      <div className="flex gap-3 p-3">
        <div className={`w-16 h-20 rounded-lg overflow-hidden flex-shrink-0 border ${isSelected ? bank.borderClass : 'border-ink-600'}`}>
          <img src={bank.advisor.portrait} alt={bank.advisor.name} className="w-full h-full object-cover object-top" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="font-bold text-sm" style={{ color: bank.color }}>{bank.name}</p>
            {loyaltyLevel >= 2 && <Star className="w-3 h-3 text-warn fill-amber-400" />}
          </div>
          <p className="text-[10px] text-slate-500">{bank.advisor.name} — {bank.advisor.title}</p>
          <p className="text-[10px] text-slate-500 italic mt-0.5">{bank.slogan}</p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: bank.accentColor + '20', color: bank.color }}>
              {getLoyaltyLabel(loyaltyLevel)}
            </span>
            {seniorityMonths !== null && seniorityMonths > 0 && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-ink-700/40 text-slate-400 font-mono" data-testid={`bank-card-seniority-${bank.id}`}>
                {seniorityMonths >= 12
                  ? `${Math.floor(seniorityMonths / 12)}a${seniorityMonths % 12 ? ` ${seniorityMonths % 12}m` : ''}`
                  : `${seniorityMonths} mois`}
              </span>
            )}
            {placements.length > 0 && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-money/15 text-money font-medium">Placement dispo</span>
            )}
            {!eligibility.allowed && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-danger/15 text-danger font-medium">Crédit limité</span>
            )}
          </div>
        </div>
      </div>
      {isSelected && (
        <div className="px-3 py-1.5 text-center text-[10px] font-semibold text-foreground" style={{ backgroundColor: bank.color }}>
          {t('bank_selected')}
        </div>
      )}
    </button>
  );
}
